import clsx from 'clsx'
import type { ReactNode } from 'react'

/**
 * Dependency-free chart primitives for the Downloads-tab previews. Plain divs sized by
 * percentage height/width, same technique OperationsDayView and the PDF's own CSS chart
 * already use elsewhere in this codebase -- no chart library exists in the project
 * (console/package.json has none), and reaching for one just for these previews would
 * grow an already-oversized bundle for a handful of bar charts.
 */

export interface BarSeries {
  key: string
  label: string
  color: string
}

export interface BarPoint {
  label: string
  values: Record<string, number>
  title?: string
}

/**
 * Grouped/single-series vertical bar chart. One column per point, one bar per series
 * inside it, scaled to the largest value across every series and point so bars are
 * comparable at a glance.
 */
export function BarChart({
  data, series, height = 120, formatValue,
}: {
  data: BarPoint[]
  series: BarSeries[]
  height?: number
  formatValue?: (v: number) => string
}): JSX.Element {
  const max = Math.max(1, ...data.flatMap((d) => series.map((s) => d.values[s.key] ?? 0)))
  const fmt = formatValue ?? ((v: number) => String(v))

  if (data.length === 0) {
    return <p className="py-6 text-center text-[11px] text-slate-400">No data in this window.</p>
  }

  return (
    <div>
      {series.length > 1 && (
        <div className="mb-2 flex flex-wrap gap-x-3 gap-y-1">
          {series.map((s) => (
            <span key={s.key} className="flex items-center gap-1.5 text-[10px] text-slate-500">
              <span className="h-2 w-2 rounded-[2px]" style={{ background: s.color }} />
              {s.label}
            </span>
          ))}
        </div>
      )}
      {/* items-stretch (the flex default -- not set explicitly before), NOT items-end: a
          column needs a real computed height to hand its own bar-row via flex-1, or every
          bar's height:{pct}% below resolves against an unresolved/auto parent height and
          renders as nothing. This was exactly that bug -- verified empty in the browser,
          not assumed from reading the code. */}
      <div className="flex gap-1.5 overflow-x-auto pb-1" style={{ height }}>
        {data.map((d, i) => (
          <div
            key={`${d.label}-${i}`}
            className="flex min-w-[18px] flex-1 flex-col items-center gap-1"
            title={d.title}
          >
            <div className="flex w-full flex-1 items-end justify-center gap-0.5">
              {series.map((s) => {
                const v = d.values[s.key] ?? 0
                const pct = Math.max(v > 0 ? 3 : 0, (v / max) * 100)
                return (
                  <div
                    key={s.key}
                    className="relative flex-1 rounded-t-[2px] transition-[height] duration-500 ease-out"
                    style={{ height: `${pct}%`, background: s.color, minWidth: 3, maxWidth: 20 }}
                  >
                    {/* bottom:100% of the bar's OWN box, not the column -- lands right above
                        this bar's top regardless of its height, no extra layout math needed. */}
                    {v > 0 && (
                      <span className="absolute bottom-full left-1/2 -translate-x-1/2 whitespace-nowrap pb-0.5 text-[8.5px] font-medium tabular-nums text-slate-500">
                        {v}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
            <span className="max-w-[36px] truncate text-[9px] text-slate-400" title={d.label}>
              {d.label}
            </span>
          </div>
        ))}
      </div>
      {formatValue && (
        <p className="mt-1 text-right text-[9.5px] text-slate-400">peak {fmt(max)}</p>
      )}
    </div>
  )
}

/** Horizontal proportion bar -- resolved vs still-open, laden vs empty, and similar
 *  two-or-three-way splits where the shape of the split matters more than exact counts. */
export function ProportionBar({
  segments, height = 8,
}: {
  segments: Array<{ label: string; value: number; color: string }>
  height?: number
}): JSX.Element {
  const total = segments.reduce((s, seg) => s + seg.value, 0)
  return (
    <div>
      <div className="flex overflow-hidden rounded-full bg-slate-100" style={{ height }}>
        {total === 0 ? (
          <div className="h-full w-full bg-slate-200" />
        ) : segments.map((seg) => (
          <div
            key={seg.label}
            className="h-full transition-[width] duration-500 ease-out"
            style={{ width: `${(seg.value / total) * 100}%`, background: seg.color }}
            title={`${seg.label}: ${seg.value}`}
          />
        ))}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
        {segments.map((seg) => (
          <span key={seg.label} className="flex items-center gap-1.5 text-[10px] text-slate-500">
            <span className="h-2 w-2 rounded-[2px]" style={{ background: seg.color }} />
            {seg.label} <span className="font-mono tabular-nums text-slate-700">{seg.value}</span>
          </span>
        ))}
      </div>
    </div>
  )
}

/** A stat tile -- the same "big mono number, quiet label" cell used throughout Reports,
 *  factored out so every preview panel's headline row looks identical. */
export function StatTile({
  label, value, unit, tone = 'default',
}: {
  label: string
  value: ReactNode
  unit?: string
  tone?: 'default' | 'danger' | 'warn' | 'ok'
}): JSX.Element {
  const toneClass = {
    default: 'text-slate-900', danger: 'text-rose-600', warn: 'text-amber-600', ok: 'text-emerald-600',
  }[tone]
  return (
    <div className="rounded-lg bg-slate-50 p-3">
      <div className={clsx('font-mono text-xl font-semibold tabular-nums', toneClass)}>
        {value}{unit && <span className="ml-0.5 text-xs font-normal text-slate-400">{unit}</span>}
      </div>
      <div className="mt-0.5 text-[10.5px] text-slate-500">{label}</div>
    </div>
  )
}
